/**
 * The admin app's screens and their URLs, on the History API. Every screen has a URL, so a staff
 * member can paste a user or a clinic into a ticket and a colleague opens the same screen.
 */
import type { FunnelStage } from "@lattice/core";
import { isFunnelStage, matchPath, pathOf } from "@lattice/core";
import { useSyncExternalStore } from "react";

export type Screen =
  | { readonly kind: "funnel"; readonly stage: FunnelStage | null }
  | { readonly kind: "search"; readonly query: string; readonly view: SearchView }
  | { readonly kind: "user"; readonly id: string }
  | { readonly kind: "organization"; readonly id: string }
  | { readonly kind: "patient"; readonly id: string }
  | { readonly kind: "jobs"; readonly view: "extractions" | "drafts" }
  | { readonly kind: "reviews" }
  | { readonly kind: "review"; readonly id: string }
  | { readonly kind: "orders"; readonly state: string }
  | { readonly kind: "pharmacyOrder"; readonly id: string }
  | { readonly kind: "reactions" }
  | { readonly kind: "catalog"; readonly ingredient: string | null; readonly creating: boolean }
  | { readonly kind: "rules"; readonly view: "versions" | "history" }
  | { readonly kind: "evaluation" }
  | {
      readonly kind: "audit";
      readonly actor: string;
      readonly patient: string;
      readonly from: string;
      readonly to: string;
    }
  | { readonly kind: "missing" };

const PATTERNS = {
  user: "/users/:id",
  organization: "/organizations/:id",
  patient: "/patients/:id",
  review: "/reviews/:id",
  pharmacyOrder: "/orders/:id",
} as const;

export type SearchView = "accounts" | "patients" | "clinics";

function searchView(value: string | null): SearchView {
  if (value === "patients" || value === "clinics") return value;

  return "accounts";
}

export const href = {
  funnel: (stage: FunnelStage | null) =>
    stage === null ? "/" : `/?${new URLSearchParams({ stage })}`,
  search: (query: string, view: SearchView = "accounts") => {
    if (query === "") return "/";
    const params = new URLSearchParams({ q: query });

    if (view !== "accounts") params.set("view", view);

    return `/?${params}`;
  },
  user: (id: string) => pathOf(PATTERNS.user, { id }),
  organization: (id: string) => pathOf(PATTERNS.organization, { id }),
  patient: (id: string) => pathOf(PATTERNS.patient, { id }),
  jobs: (view: "extractions" | "drafts" = "extractions") =>
    view === "drafts" ? "/jobs?view=drafts" : "/jobs",
  reviews: () => "/reviews",
  review: (id: string) => pathOf(PATTERNS.review, { id }),
  orders: (state = "") => (state === "" ? "/orders" : `/orders?${new URLSearchParams({ state })}`),
  pharmacyOrder: (id: string) => pathOf(PATTERNS.pharmacyOrder, { id }),
  reactions: () => "/reactions",
  catalog: () => "/catalog",
  ingredient: (id: string) => `/catalog?${new URLSearchParams({ ingredient: id })}`,
  newIngredient: () => "/catalog?new=1",
  rules: (view: "versions" | "history" = "versions") =>
    view === "history" ? "/rules?view=history" : "/rules",
  evaluation: () => "/evaluation",
  audit: (
    filter: {
      readonly actor?: string;
      readonly patient?: string;
      readonly from?: string;
      readonly to?: string;
    } = {},
  ) => {
    const params = new URLSearchParams();

    if (filter.actor) params.set("actor", filter.actor);

    if (filter.patient) params.set("patient", filter.patient);

    if (filter.from) params.set("from", filter.from);

    if (filter.to) params.set("to", filter.to);

    const query = params.toString();

    return query === "" ? "/audit" : `/audit?${query}`;
  },
};

function exactScreen(pathname: string): Screen | null {
  if (pathname === "/jobs") return null;

  if (pathname === "/reviews") return { kind: "reviews" };

  if (pathname === "/reactions") return { kind: "reactions" };

  if (pathname === "/catalog") return null;

  if (pathname === "/rules") return null;

  if (pathname === "/evaluation") return { kind: "evaluation" };

  return null;
}

function homeScreen(search: string): Screen {
  const params = new URLSearchParams(search);
  const query = params.get("q");

  if (query !== null && query !== "")
    return { kind: "search", query, view: searchView(params.get("view")) };

  const stage = params.get("stage");

  return { kind: "funnel", stage: isFunnelStage(stage) ? stage : null };
}

function listedScreen(pathname: string, search: string): Screen | null {
  const view = new URLSearchParams(search).get("view");

  if (pathname === "/jobs")
    return { kind: "jobs", view: view === "drafts" ? "drafts" : "extractions" };

  if (pathname === "/rules")
    return { kind: "rules", view: view === "history" ? "history" : "versions" };

  if (pathname === "/orders")
    return { kind: "orders", state: new URLSearchParams(search).get("state") ?? "" };

  if (pathname === "/catalog") {
    const params = new URLSearchParams(search);
    const ingredient = params.get("ingredient");

    return {
      kind: "catalog",
      ingredient: ingredient === null || ingredient === "" ? null : ingredient,
      creating: params.get("new") === "1",
    };
  }

  return null;
}

function screenAt(pathname: string, search: string): Screen {
  if (pathname === "/") return homeScreen(search);

  const listed = listedScreen(pathname, search);

  if (listed) return listed;

  if (pathname === "/audit") {
    const params = new URLSearchParams(search);

    return {
      kind: "audit",
      actor: params.get("actor") ?? "",
      patient: params.get("patient") ?? "",
      from: params.get("from") ?? "",
      to: params.get("to") ?? "",
    };
  }

  const exact = exactScreen(pathname);

  if (exact) return exact;

  for (const kind of ["user", "organization", "patient", "review", "pharmacyOrder"] as const) {
    const id = matchPath(PATTERNS[kind], pathname)?.["id"];

    if (id !== undefined) return { kind, id };
  }

  return { kind: "missing" };
}

/* The last search shown, so a detail page can lead back to its results. Lost on reload. */
let lastQuery = "";

/* The last list a detail page was opened from: signups, a funnel step, or search results. */
let lastListPath = "/";

export function rememberSearch(query: string): void {
  lastQuery = query;
}

export function lastSearch(): string {
  return lastQuery;
}

export function rememberList(path: string): void {
  lastListPath = path;
}

export function lastList(): string {
  return lastListPath;
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("popstate", listener);

  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

/** The URL as one string, which useSyncExternalStore can compare between renders. */
function snapshot(): string {
  return window.location.pathname + window.location.search;
}

export function navigate(to: string, mode: "push" | "replace" = "push"): void {
  if (to === snapshot()) return;

  if (mode === "push") window.history.pushState(null, "", to);
  else window.history.replaceState(null, "", to);

  for (const listener of listeners) listener();

  if (mode === "push") window.scrollTo(0, 0);
}

export function useScreen(): Screen {
  const url = useSyncExternalStore(subscribe, snapshot);
  const [pathname = "/", search = ""] = url.split(/(?=\?)/);

  return screenAt(pathname, search);
}
